import { z } from "zod";

// Enums for role hierarchy
export enum RoleLevel {
  ADMIN = 1,           // Highest access
  MANAGER = 2,         // Team/Department level
  TEAM_LEAD = 3,       // Sub-team level  
  SALES_REP = 4,       // Individual contributor
  VIEW_ONLY = 5        // Read-only access
}

export enum LeadScope {
  ALL = "all",                    // Admin sees everything
  TEAM = "team",                  // Manager sees their team + sub-teams  
  ASSIGNED = "assigned",          // Individual sees only assigned leads
  DEPARTMENT = "department",      // Department-level access
  NONE = "none"                   // No access
}

export enum AccessLevel {
  NONE = "none",
  OWN = "own",
  TEAM = "team", 
  ALL = "all"
}

// Structured permissions interface
export interface StructuredPermissions {
  leads: {
    create: boolean;
    view: LeadScope;
    edit: AccessLevel;
    delete: AccessLevel;
    assign: boolean;
    export: boolean;
    import: boolean;
  };
  contacts: {
    create: boolean;
    view: AccessLevel;
    edit: AccessLevel;
    delete: AccessLevel;
  };
  reports: {
    view_personal: boolean;
    view_team: boolean;
    view_all: boolean;
    export: boolean;
  };
  administration: {
    user_management: boolean;
    role_management: boolean;
    form_management: boolean;
    system_settings: boolean;
  };
  dashboard: {
    view_personal: boolean;
    view_team: boolean;
    view_company: boolean;
  };
}

// Enhanced role detail interface
export interface EnhancedRoleDetail {
  uuid: string;
  role_name: string;
  role_description: string | null;
  role_level: RoleLevel;
  lead_scope: LeadScope;
  permissions: StructuredPermissions;
  is_system_role: boolean;
  is_active: boolean;
  pipeline_uuid?: string;
  pipeline_id_fk?: number;
  created_at?: Date;
  updated_at?: Date;
  users_count?: number;
}

// Role template interface for predefined roles
export interface RoleTemplate {
  id: string;
  name: string;
  description: string;
  level: RoleLevel;
  lead_scope: LeadScope;
  permissions: StructuredPermissions;
  is_system_role: boolean;
  icon: string;
  color: string;
  category: 'administrative' | 'management' | 'sales' | 'support';
}

// Form schema for enhanced roles
export const EnhancedRoleFormSchema = z.object({
  roleName: z.string().min(2, "Role name must be at least 2 characters").max(50, "Role name must be less than 50 characters"),
  roleDescription: z.string().nullable(),
  roleLevel: z.nativeEnum(RoleLevel),
  leadScope: z.nativeEnum(LeadScope),
  useTemplate: z.boolean().default(false),
  templateId: z.string().optional(),
  customPermissions: z.object({
    leads: z.object({
      create: z.boolean().default(false),
      view: z.nativeEnum(LeadScope).default(LeadScope.NONE),
      edit: z.nativeEnum(AccessLevel).default(AccessLevel.NONE),
      delete: z.nativeEnum(AccessLevel).default(AccessLevel.NONE),
      assign: z.boolean().default(false),
      export: z.boolean().default(false),
      import: z.boolean().default(false),
    }),
    contacts: z.object({
      create: z.boolean().default(false),
      view: z.nativeEnum(AccessLevel).default(AccessLevel.NONE),
      edit: z.nativeEnum(AccessLevel).default(AccessLevel.NONE),
      delete: z.nativeEnum(AccessLevel).default(AccessLevel.NONE),
    }),
    reports: z.object({
      view_personal: z.boolean().default(false),
      view_team: z.boolean().default(false),
      view_all: z.boolean().default(false),
      export: z.boolean().default(false),
    }),
    administration: z.object({
      user_management: z.boolean().default(false),
      role_management: z.boolean().default(false),
      form_management: z.boolean().default(false),
      system_settings: z.boolean().default(false),
    }),
    dashboard: z.object({
      view_personal: z.boolean().default(false),
      view_team: z.boolean().default(false),
      view_company: z.boolean().default(false),
    }),
  }).optional(),
});

export type EnhancedRoleFormData = z.infer<typeof EnhancedRoleFormSchema>;

// User hierarchy interfaces
export interface UserHierarchy {
  user_uuid: string;
  manager_uuid: string | null;
  department: string | null;
  team_name: string | null;
  reporting_level: number;
  effective_from: Date;
  effective_to: Date | null;
}

export interface TeamAssignment {
  team_id: string;
  team_name: string;
  user_uuid: string;
  role_in_team: 'lead' | 'member';
  assigned_by: string;
  assigned_at: Date;
  is_active: boolean;
}

// API response interfaces
export interface EnhancedRolesApiData {
  roles: EnhancedRoleDetail[];
  templates: RoleTemplate[];
  pagination: {
    currentPage: number;
    totalPages: number;
    totalItems: number;
    itemsPerPage: number;
    hasNextPage: boolean;
    hasPreviousPage: boolean;
  };
}

export interface RoleCreateUpdateRequest {
  role_name: string;
  role_description: string | null;
  role_level: RoleLevel;
  lead_scope: LeadScope;
  permissions: StructuredPermissions;
  is_system_role?: boolean;
}